import { EmbeddedBlock, EmbeddedBlockVariant } from '@tinct/core-api-models';
import { type BlockVariantRenderer, type BlockVariantRendering } from './types';
import { renderDefault } from './default';
import { customerLogoRenderer } from './customer-logo';

export * from './types';

/**
 * Dedicated per-type renderers. Register a new block type by adding its
 * renderer here (one renderer file per block type).
 */
const RENDERERS: BlockVariantRenderer[] = [customerLogoRenderer];

/**
 * Whether a variant can be edited inline on the preview (useInlineTextEditor):
 * only default-rendered variants — plain HTML written via innerHTML — qualify.
 * Types with a dedicated renderer (customer_logo's React widget, and any
 * future one) manage their own DOM and are excluded automatically.
 */
export function isInlineEditableVariant(variant: EmbeddedBlockVariant): boolean {
  return (
    !!variant.id &&
    !!variant.cssSelectors &&
    !RENDERERS.some((renderer) => renderer.type === variant.type)
  );
}

/**
 * Resolves and runs the renderer for a variant: the dedicated renderer for its
 * `type` if one is registered, falling back to the default (HTML) renderer when
 * there's no match or the dedicated renderer opts out (returns `null`). A
 * `skip` result leaves the element untouched (no fallback).
 */
export function renderBlockVariant(
  variant: EmbeddedBlockVariant,
  block: EmbeddedBlock
): BlockVariantRendering | null {
  const renderer = RENDERERS.find((r) => r.type === variant.type);
  const rendering = renderer?.render(variant, block);
  if (rendering?.kind === 'skip') return null;
  if (rendering) {
    return renderer?.elementStyle
      ? { ...rendering, elementStyle: renderer.elementStyle }
      : rendering;
  }
  return renderDefault(variant);
}
